import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { colors, radius, shadows, styles } from '../styles/theme';
import { normalizeForSearch, searchMatchPre } from '../utils/searchMatch';
import { logError } from '../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const STATUS_CONFIG = {
  PENDING:    { label: 'Onay Bekliyor', color: '#92400e', bg: '#fef3c7', actions: ['approve', 'reject'] },
  APPROVED:   { label: 'Onaylandı',     color: '#1e40af', bg: '#dbeafe', actions: ['ship'] },
  SHIPPED:    { label: 'Sevk Edildi',    color: '#7c3aed', bg: '#f3e8ff', actions: ['receive'] },
  COMPLETED:  { label: 'Tamamlandı',    color: '#166534', bg: '#dcfce7', actions: [] },
  RECEIVED:   { label: 'Teslim Alındı', color: '#166534', bg: '#dcfce7', actions: [] },
  REJECTED:   { label: 'Reddedildi',    color: '#991b1b', bg: '#fee2e2', actions: [] },
  CANCELLED:  { label: 'İptal',         color: '#6b7280', bg: '#f3f4f6', actions: [] },
};

const ACTION_CONFIG = {
  approve: { label: 'Onayla',    bg: '#16a34a', endpoint: 'approve' },
  reject:  { label: 'Reddet',    bg: '#dc2626', endpoint: 'reject' },
  ship:    { label: 'Sevk Et',   bg: '#7c3aed', endpoint: 'ship' },
  receive: { label: 'Teslim Al', bg: '#16a34a', endpoint: 'receive' },
};

const formatNum = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Liste tablosunda Birim Maliyet / Transfer Fiyatı hücresi.
// estimated=true → henüz sevk edilmemiş transferin canlı tahmini (soluk + "tahmini" rozet).
const renderListPrice = (value, estimated, withBadge = false) => {
  if (!value) return '-';
  return (
    <span
      style={{ color: estimated ? colors.textSecondary : 'inherit', whiteSpace: 'nowrap' }}
      title={estimated ? 'Tahmini fiyat — sevk anında kesinleşir' : undefined}
    >
      {formatNum(value)} TL
      {estimated && withBadge && (
        <span style={{
          marginLeft: 5, fontSize: '9px', fontWeight: 700, letterSpacing: '0.02em',
          color: '#92400e', background: '#fef3c7', padding: '1px 5px',
          borderRadius: radius.full, verticalAlign: 'middle',
        }}>tahmini</span>
      )}
    </span>
  );
};
const formatDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const InventoryPage = () => {
  const { user } = useAuth();
  const [view, setView] = useState('list');
  const [locations, setLocations] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [dateSort, setDateSort] = useState('desc'); // desc: yeni→eski, asc: eski→yeni
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterDest, setFilterDest] = useState('');
  const [filterMalzemeId, setFilterMalzemeId] = useState('');
  const [filterMalzemeSearch, setFilterMalzemeSearch] = useState('');
  const [filterMalzemeOpen, setFilterMalzemeOpen] = useState(false);

  // Form state
  const [sourceId, setSourceId] = useState('');
  const [destId, setDestId] = useState('');
  const [materialId, setMaterialId] = useState('');
  const [materialSearch, setMaterialSearch] = useState('');
  const [materialDropdownOpen, setMaterialDropdownOpen] = useState(false);
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [transferTarihi, setTransferTarihi] = useState(new Date().toISOString().split('T')[0]);
  const [transferPrice, setTransferPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Suppress unused warning for user (used implicitly for auth)
  void user;

  // Transfer fiyatları cache
  const [allTransferPrices, setAllTransferPrices] = useState([]);

  // --- Data fetch ---
  useEffect(() => {
    const loadMaster = async () => {
      try {
        const [locRes, matRes, priceRes] = await Promise.all([
          fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/api/malzemeler`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/api/transfer-prices`, { headers: getAuthHeaders() }),
        ]);
        if (locRes.ok) setLocations(await locRes.json());
        if (matRes.ok) setMaterials(await matRes.json());
        if (priceRes.ok) setAllTransferPrices(await priceRes.json());
      } catch (err) { logError('Master data load error', err); }
    };
    loadMaster();
  }, []);

  // Close material dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('[data-material-search]')) setMaterialDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterSource) params.set('source_id', filterSource);
      if (filterDest) params.set('destination_id', filterDest);
      if (filterMalzemeId) params.set('malzeme_id', filterMalzemeId);
      params.set('limit', '200');
      const res = await fetch(`${API_URL}/api/inventory/transfers?${params}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setTransfers(Array.isArray(data) ? data : (data.transfers || []));
      }
    } catch (e) { logError('Transfer list error:', e); }
    finally { setLoading(false); }
  }, [filterStatus, filterSource, filterDest, filterMalzemeId]);

  useEffect(() => {
    if (view === 'list') loadTransfers();
  }, [view, loadTransfers]);

  // --- KPI calculations ---
  const kpis = useMemo(() => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    let pending = 0;
    let shipped = 0;
    let completedThisMonth = 0;
    let totalRevenueThisMonth = 0;

    transfers.forEach(t => {
      if (t.status === 'PENDING') pending++;
      if (t.status === 'SHIPPED') shipped++;
      if ((t.status === 'COMPLETED' || t.status === 'RECEIVED') && t.received_at) {
        const d = new Date(t.received_at);
        if (d.getMonth() === thisMonth && d.getFullYear() === thisYear) {
          completedThisMonth++;
          if (t.internal_sale && t.internal_sale.total_revenue) {
            totalRevenueThisMonth += Number(t.internal_sale.total_revenue);
          }
        }
      }
    });
    return { pending, shipped, completedThisMonth, totalRevenueThisMonth };
  }, [transfers]);

  // --- Expand row ---
  const toggleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); setExpandedDetail(null); return; }
    setExpandedId(id);
    setExpandedDetail(null);
    try {
      const res = await fetch(`${API_URL}/api/inventory/transfers/${id}`, { headers: getAuthHeaders() });
      if (res.ok) setExpandedDetail(await res.json());
    } catch (e) { logError('Detail error:', e); }
  };

  // --- Workflow actions ---
  const handleAction = async (transferId, action) => {
    const config = ACTION_CONFIG[action];
    if (!config) return;
    const confirmed = window.confirm(`Bu transferi "${config.label}" olarak işaretlemek istediğinizden emin misiniz?`);
    if (!confirmed) return;
    setActionLoading(`${transferId}-${action}`);
    try {
      let res = await fetch(`${API_URL}/api/inventory/transfers/${transferId}/${config.endpoint}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
      });

      // Sevk aşamasında yetersiz stok → onayla geçirme seçeneği
      if (!res.ok && action === 'ship' && res.status === 400) {
        const err = await res.json().catch(() => ({}));
        const detail = err.detail || '';
        if (detail.includes('Yetersiz stok') || detail.includes('yetersiz stok')) {
          const forceConfirm = window.confirm(
            `${detail}\n\nSayımın güncel olmayabileceğini kabul ederek yine de sevk etmek istiyor musun?\n` +
            `(Stok negatife düşer — sonraki sayımla düzelir, işleme [FORCE SEVK] notu düşer)`
          );
          if (forceConfirm) {
            res = await fetch(`${API_URL}/api/inventory/transfers/${transferId}/${config.endpoint}?force=true`, {
              method: 'PATCH',
              headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
            });
          } else {
            setMessage({ type: 'warning', text: 'Sevk iptal edildi. Önce kaynak lokasyonda sayım yapmayı düşün.' });
            setActionLoading(null);
            return;
          }
        }
      }

      if (res.ok) {
        setMessage({ type: 'success', text: `Transfer #${transferId} başarıyla güncellendi.` });
        loadTransfers();
        if (expandedId === transferId) {
          setExpandedId(null);
          setExpandedDetail(null);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        setMessage({ type: 'danger', text: `Hata: ${err.detail || 'İşlem başarısız'}` });
      }
    } catch (e) {
      setMessage({ type: 'danger', text: 'Bağlantı hatası' });
    } finally { setActionLoading(null); }
  };

  // --- Transfer price lookup (from cached data) ---
  useEffect(() => {
    if (!materialId) { setTransferPrice(null); return; }
    setPriceLoading(true);
    const mid = parseInt(materialId);
    const found = allTransferPrices.find(p => p.malzeme_id === mid);
    setTransferPrice(found || null);
    setPriceLoading(false);
  }, [materialId, allTransferPrices]);

  // --- Create transfer ---
  const handleCreateTransfer = async (e) => {
    e.preventDefault();
    if (sourceId === destId) {
      setMessage({ type: 'danger', text: 'Kaynak ve hedef lokasyon aynı olamaz.' });
      return;
    }
    setFormLoading(true);
    setMessage(null);
    const payload = {
      source_id: parseInt(sourceId),
      destination_id: parseInt(destId),
      malzeme_id: parseInt(materialId),
      quantity: parseFloat(quantity),
      transfer_tarihi: transferTarihi || null,
    };
    if (notes.trim()) payload.notes = notes.trim();
    try {
      let res = await fetch(`${API_URL}/api/inventory/transfers`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      // Yetersiz stok → onayla geçirme
      if (!res.ok && res.status === 400) {
        const err = await res.json().catch(() => ({}));
        const detail = err.detail || '';
        if (detail.includes('Yetersiz stok') || detail.includes('yetersiz stok')) {
          const forceConfirm = window.confirm(
            `${detail}\n\nSayım güncel olmayabilir. Yine de transfer talebi oluşturmak istiyor musun?\n` +
            `(Talep [FORCE TALEP] notu ile oluşur — sevk aşamasında yine kontrol edilir)`
          );
          if (forceConfirm) {
            res = await fetch(`${API_URL}/api/inventory/transfers?force=true`, {
              method: 'POST',
              headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            });
          } else {
            setMessage({ type: 'warning', text: 'Transfer talebi iptal edildi.' });
            setFormLoading(false);
            return;
          }
        }
      }

      if (res.ok) {
        setMessage({ type: 'success', text: 'Transfer talebi başarıyla oluşturuldu!' });
        setSourceId(''); setDestId(''); setMaterialId(''); setMaterialSearch('');
        setQuantity(''); setNotes(''); setTransferTarihi(new Date().toISOString().split('T')[0]); setTransferPrice(null);
        setView('list');
      } else {
        const err = await res.json().catch(() => ({}));
        setMessage({ type: 'danger', text: `Hata: ${err.detail || 'Bilinmeyen hata'}` });
      }
    } catch { setMessage({ type: 'danger', text: 'Bağlantı hatası' }); }
    finally { setFormLoading(false); }
  };

  // --- Filtered materials (form) ---
  const filteredMaterials = useMemo(() => {
    if (!materialSearch || materialSearch.length < 1) return [];
    const q = normalizeForSearch(materialSearch);
    return materials
      .filter(m => searchMatchPre(m.ad, q) || searchMatchPre(m.kod, q))
      .slice(0, 30);
  }, [materials, materialSearch]);

  // --- Filtered materials (filter bar) ---
  const filterMalzemeResults = useMemo(() => {
    if (!filterMalzemeSearch || filterMalzemeSearch.length < 1) return [];
    const q = normalizeForSearch(filterMalzemeSearch);
    return materials
      .filter(m => searchMatchPre(m.ad, q) || searchMatchPre(m.kod, q))
      .slice(0, 30);
  }, [materials, filterMalzemeSearch]);

  const filterMalzemeSelected = filterMalzemeId
    ? materials.find(m => String(m.id) === String(filterMalzemeId))
    : null;
   // --- Tarihe göre sıralama ---
   const sortedTransfers = useMemo(() => {
     return [...transfers].sort((a, b) => {
       const dateA = new Date(a.transfer_tarihi || a.created_at || 0).getTime();
       const dateB = new Date(b.transfer_tarihi || b.created_at || 0).getTime();
    
       // Aynı tarihte en yeni kayıt/id üstte olsun
       if (dateA === dateB) {
         return dateSort === 'desc'
           ? Number(b.id || 0) - Number(a.id || 0)
           : Number(a.id || 0) - Number(b.id || 0);
       }
    
       return dateSort === 'desc'
         ? dateB - dateA
         : dateA - dateB;
     });
   }, [transfers, dateSort]);
   
  // estimated total
  const estimatedTotal = useMemo(() => {
    if (!transferPrice || !quantity) return null;
    const price = transferPrice.transfer_fiyati || transferPrice.uretim_maliyeti;
    if (!price) return null;
    return parseFloat(quantity) * price;
  }, [transferPrice, quantity]);

  // --- Message auto-dismiss ---
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(t);
  }, [message]);

  // ======================== RENDER ========================

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Message */}
      {message && (
        <div style={{
          padding: '12px 16px', marginBottom: '16px', borderRadius: radius.sm,
          background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#166534' : '#991b1b',
          border: `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          fontSize: '13px', fontWeight: 500,
        }}>
          {message.text}
        </div>
      )}

      {view === 'list' && renderListView()}
      {view === 'form' && renderFormView()}
    </div>
  );

  // ======================== LIST VIEW ========================
  function renderListView() {
    return (
      <>
        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
          {renderKpiCard('Bekleyen Transfer', kpis.pending, '#92400e', '#fef3c7')}
          {renderKpiCard('Sevk Edilen', kpis.shipped, '#7c3aed', '#f3e8ff')}
          {renderKpiCard('Bu Ay Tamamlanan', kpis.completedThisMonth, '#166534', '#dcfce7')}
          {renderKpiCard('Bu Ay Toplam Tutar', `${formatNum(kpis.totalRevenueThisMonth)} TL`, '#1e40af', '#dbeafe')}
        </div>

        {/* Filters + New button */}
        <div style={{
          background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md,
          padding: '16px', marginBottom: '16px', boxShadow: shadows.sm,
        }}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 160px' }}>
              <label style={labelSmall}>Durum</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
                <option value="">Tum durumlar</option>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <label style={labelSmall}>Kaynak</label>
              <select value={filterSource} onChange={e => setFilterSource(e.target.value)} style={selectStyle}>
                <option value="">Tum lokasyonlar</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <label style={labelSmall}>Hedef</label>
              <select value={filterDest} onChange={e => setFilterDest(e.target.value)} style={selectStyle}>
                <option value="">Tum lokasyonlar</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 200px', position: 'relative' }}>
              <label style={labelSmall}>Ürün</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={filterMalzemeSelected ? `${filterMalzemeSelected.kod} - ${filterMalzemeSelected.ad}` : filterMalzemeSearch}
                  onChange={e => {
                    setFilterMalzemeSearch(e.target.value);
                    setFilterMalzemeId('');
                    setFilterMalzemeOpen(true);
                  }}
                  onFocus={() => setFilterMalzemeOpen(true)}
                  onBlur={() => setTimeout(() => setFilterMalzemeOpen(false), 150)}
                  placeholder="Ürün ara..."
                  style={{ ...selectStyle, paddingRight: filterMalzemeId ? '28px' : '8px', cursor: 'text' }}
                />
                {filterMalzemeId && (
                  <span
                    onClick={() => { setFilterMalzemeId(''); setFilterMalzemeSearch(''); }}
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#999', fontSize: 14 }}
                  >✕</span>
                )}
              </div>
              {filterMalzemeOpen && filterMalzemeResults.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
                  background: '#fff', border: '1px solid #e0e0e0', borderRadius: '0 0 6px 6px',
                  boxShadow: shadows.md, maxHeight: 220, overflowY: 'auto'
                }}>
                  {filterMalzemeResults.map(m => (
                    <div
                      key={m.id}
                      onMouseDown={() => {
                        setFilterMalzemeId(String(m.id));
                        setFilterMalzemeSearch('');
                        setFilterMalzemeOpen(false);
                      }}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <span style={{ color: '#666', marginRight: 6 }}>{m.kod}</span>{m.ad}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={loadTransfers} style={secondaryBtnStyle}>Yenile</button>
            <button onClick={() => setView('form')} style={primaryBtnStyle}>+ Yeni Transfer</button>
          </div>
        </div>

        {/* Table */}
        <div style={{
          background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md,
          boxShadow: shadows.sm, overflow: 'hidden',
        }}>
          {loading && transfers.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: colors.textTertiary }}>Yukleniyor...</div>
          ) : transfers.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: colors.textTertiary }}>Transfer kaydi bulunamadi</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: colors.backgroundAlt }}>
                    
                    <th
                      style={{
                        ...thStyle,
                        cursor: 'pointer',
                        userSelect: 'none',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={() => setDateSort(prev => prev === 'desc' ? 'asc' : 'desc')}
                      title={dateSort === 'desc' ? 'Eskiden yeniye sırala' : 'Yeniden eskiye sırala'}
                    >
                      Tarih {dateSort === 'desc' ? '↓' : '↑'}
                    </th>
                    
                    <th style={thStyle}>Kaynak &rarr; Hedef</th>
                    <th style={thStyle}>Malzeme</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Miktar</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Birim Maliyet</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Transfer Fiyatı</th>
                    <th style={thStyle}>Durum</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Islemler</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTransfers.map(t => {
                    const st = STATUS_CONFIG[t.status] || { label: t.status, color: '#333', bg: '#eee', actions: [] };
                    const isExpanded = expandedId === t.id;
                    return (
                      <React.Fragment key={t.id}>
                        <tr
                          onClick={() => toggleExpand(t.id)}
                          style={{
                            borderBottom: `1px solid ${colors.border}`,
                            cursor: 'pointer',
                            background: isExpanded ? colors.backgroundAlt : 'transparent',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#fafbfc'; }}
                          onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <td style={tdStyle}>{t.transfer_tarihi || formatDate(t.created_at)}</td>
                          <td style={tdStyle}>
                            <span style={{ fontWeight: 500 }}>{t.source_name || t.source_ad || `#${t.source_id}`}</span>
                            <span style={{ color: colors.textTertiary, margin: '0 6px' }}>&rarr;</span>
                            <span style={{ fontWeight: 500 }}>{t.destination_name || t.destination_ad || `#${t.destination_id}`}</span>
                          </td>
                          <td style={tdStyle}>{t.malzeme_name || t.malzeme_ad || t.malzeme_id}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500 }}>
                            {formatNum(t.quantity || t.miktar)} {t.unit || t.birim || ''}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            {renderListPrice(t.unit_cost_price, t.price_estimated)}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                            {renderListPrice(t.transfer_price, t.price_estimated, true)}
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              display: 'inline-block', padding: '3px 10px', borderRadius: radius.full,
                              background: st.bg, color: st.color, fontSize: '11px', fontWeight: 600,
                            }}>
                              {st.label}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                            {renderActionButtons(t, st)}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan="8" style={{ padding: 0, borderBottom: `1px solid ${colors.border}` }}>
                              {renderExpandedDetail(t)}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>
    );
  }

  function renderKpiCard(label, value, textColor, bgColor) {
    return (
      <div style={{
        background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md,
        padding: '16px 20px', boxShadow: shadows.sm,
      }}>
        <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textTertiary, marginBottom: '6px' }}>
          {label}
        </div>
        <div style={{ fontSize: '24px', fontWeight: 700, color: textColor }}>
          <span style={{ background: bgColor, padding: '2px 8px', borderRadius: radius.sm }}>{value}</span>
        </div>
      </div>
    );
  }

  function renderActionButtons(t, st) {
    const actions = st.actions || [];
    if (actions.length === 0) return null;
    return (
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
        {actions.map(action => {
          const cfg = ACTION_CONFIG[action];
          if (!cfg) return null;
          const isLoading = actionLoading === `${t.id}-${action}`;
          return (
            <button
              key={action}
              onClick={() => handleAction(t.id, action)}
              disabled={isLoading}
              style={{
                padding: '4px 12px', fontSize: '11px', fontWeight: 600,
                border: 'none', borderRadius: radius.sm, cursor: isLoading ? 'not-allowed' : 'pointer',
                color: '#fff', background: isLoading ? '#ccc' : cfg.bg,
                transition: 'opacity 0.15s', whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            >
              {isLoading ? '...' : cfg.label}
            </button>
          );
        })}
      </div>
    );
  }

  function renderExpandedDetail(t) {
    const detail = expandedDetail && expandedDetail.id === t.id ? expandedDetail : t;
    const sale = detail.internal_sale;
    return (
      <div style={{ padding: '20px 24px', background: '#f8fafc', borderTop: `1px solid ${colors.border}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Transfer Info */}
          <div>
            <div style={sectionTitleStyle}>Transfer Bilgileri</div>
            <div style={detailGridStyle}>
              <div><span style={detailLabelStyle}>Kaynak:</span> {detail.source_name || detail.source_ad || detail.source_id}</div>
              <div><span style={detailLabelStyle}>Hedef:</span> {detail.destination_name || detail.destination_ad || detail.destination_id}</div>
              <div><span style={detailLabelStyle}>Malzeme:</span> {detail.malzeme_name || detail.malzeme_ad || detail.malzeme_id}</div>
              <div><span style={detailLabelStyle}>Miktar:</span> {formatNum(detail.quantity || detail.miktar)} {detail.unit || detail.birim || ''}</div>
              {detail.notes && (
                <div style={{ gridColumn: '1 / 3' }}><span style={detailLabelStyle}>Not:</span> {detail.notes}</div>
              )}
            </div>
          </div>

          {/* Financial Info */}
          {sale && (
            <div>
              <div style={sectionTitleStyle}>Finansal Bilgi</div>
              <div style={detailGridStyle}>
                <div><span style={detailLabelStyle}>Birim Maliyet:</span> {formatNum(sale.unit_cost_price)} TL</div>
                <div><span style={detailLabelStyle}>Transfer Fiyati:</span> {formatNum(sale.transfer_price)} TL</div>
                <div><span style={detailLabelStyle}>Toplam Maliyet:</span> {formatNum(sale.total_cost)} TL</div>
                <div><span style={detailLabelStyle}>Toplam Gelir:</span> {formatNum(sale.total_revenue)} TL</div>
                <div style={{ gridColumn: '1 / 3' }}>
                  <span style={detailLabelStyle}>Kar:</span>{' '}
                  <span style={{ fontWeight: 700, color: (sale.theoretical_profit || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                    {formatNum(sale.theoretical_profit)} TL
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div style={{ marginTop: '20px' }}>
          <div style={sectionTitleStyle}>Zaman Cizelgesi</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '12px' }}>
            {renderTimelineStep('Olusturan', detail.requester_name, detail.created_at, true)}
            {renderTimelineArrow()}
            {renderTimelineStep('Onaylayan', detail.approver_name, detail.approved_at, !!detail.approved_at)}
            {renderTimelineArrow()}
            {renderTimelineStep('Sevk', null, detail.shipped_at, !!detail.shipped_at)}
            {renderTimelineArrow()}
            {renderTimelineStep('Teslim', null, detail.received_at, !!detail.received_at)}
          </div>
        </div>
      </div>
    );
  }

  function renderTimelineStep(label, name, date, active) {
    return (
      <div style={{
        padding: '8px 14px', borderRadius: radius.sm,
        background: active ? '#dbeafe' : '#f1f5f9',
        color: active ? '#1e40af' : colors.textTertiary,
        border: `1px solid ${active ? '#93c5fd' : colors.border}`,
      }}>
        <div style={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        {name && <div style={{ fontWeight: 500, fontSize: '12px' }}>{name}</div>}
        <div style={{ fontSize: '11px' }}>{date ? formatDate(date) : '-'}</div>
      </div>
    );
  }

  function renderTimelineArrow() {
    return <div style={{ display: 'flex', alignItems: 'center', color: colors.textTertiary, fontSize: '16px' }}>&rarr;</div>;
  }

  // ======================== FORM VIEW ========================
  function renderFormView() {
    return (
      <div style={{
        background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md,
        padding: '24px', boxShadow: shadows.sm, maxWidth: '700px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>Yeni Transfer Talebi</h3>
          <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary, fontSize: '14px' }}>
            &larr; Listeye Don
          </button>
        </div>

        <form onSubmit={handleCreateTransfer}>
          {/* Source + Dest */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={formLabelStyle}>Kaynak Lokasyon</label>
              <select value={sourceId} onChange={e => setSourceId(e.target.value)} required style={formInputStyle}>
                <option value="">Seciniz...</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
              </select>
            </div>
            <div>
              <label style={formLabelStyle}>Hedef Lokasyon</label>
              <select value={destId} onChange={e => setDestId(e.target.value)} required style={formInputStyle}>
                <option value="">Seciniz...</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
              </select>
            </div>
          </div>

          {/* Material search */}
          <div style={{ marginBottom: '16px' }}>
            <label style={formLabelStyle}>Malzeme</label>
            <div data-material-search style={{ position: 'relative' }}>
              <input
                type="text"
                value={materialSearch}
                onChange={e => {
                  setMaterialSearch(e.target.value);
                  setMaterialDropdownOpen(true);
                  if (!e.target.value) { setMaterialId(''); setTransferPrice(null); }
                }}
                onFocus={() => { if (materialSearch.length >= 1) setMaterialDropdownOpen(true); }}
                placeholder="Malzeme ara..."
                style={formInputStyle}
                autoComplete="off"
                required={!materialId}
              />
              {materialId && (
                <button
                  type="button"
                  onClick={() => { setMaterialId(''); setMaterialSearch(''); setTransferPrice(null); }}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#999' }}
                >
                  x
                </button>
              )}
              {materialDropdownOpen && filteredMaterials.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                  background: colors.surface, border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm, maxHeight: '250px', overflowY: 'auto',
                  boxShadow: shadows.md,
                }}>
                  {filteredMaterials.map(m => (
                    <div
                      key={m.id}
                      onClick={() => {
                        setMaterialId(String(m.id));
                        setMaterialSearch(`${m.ad} (${m.birim})`);
                        setMaterialDropdownOpen(false);
                      }}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                        borderBottom: `1px solid ${colors.borderLight}`,
                        background: String(m.id) === materialId ? '#e3f2fd' : 'transparent',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = String(m.id) === materialId ? '#e3f2fd' : 'transparent'; }}
                    >
                      <div style={{ fontWeight: 500 }}>{m.ad}</div>
                      <div style={{ fontSize: '11px', color: '#888' }}>{m.kod || '-'} - {m.birim}</div>
                    </div>
                  ))}
                </div>
              )}
              {materialDropdownOpen && materialSearch.length >= 1 && filteredMaterials.length === 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                  background: colors.surface, border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm, boxShadow: shadows.md,
                  padding: '12px', color: '#999', textAlign: 'center', fontSize: '13px',
                }}>
                  Sonuc bulunamadi
                </div>
              )}
            </div>
          </div>

          {/* Transfer price info */}
          {materialId && (
            <div style={{
              marginBottom: '16px', padding: '14px', borderRadius: radius.sm,
              background: transferPrice ? '#f0fdf4' : '#fef3c7',
              border: `1px solid ${transferPrice ? '#bbf7d0' : '#fde68a'}`,
              fontSize: '13px',
            }}>
              {priceLoading ? (
                <span style={{ color: colors.textTertiary }}>Fiyat bilgisi yukleniyor...</span>
              ) : transferPrice ? (
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  <div>
                    <span style={{ color: colors.textSecondary }}>Birim Maliyet:</span>{' '}
                    <strong>{formatNum(transferPrice.uretim_maliyeti)} TL</strong>
                  </div>
                  <div>
                    <span style={{ color: colors.textSecondary }}>Transfer Fiyati:</span>{' '}
                    <strong>{formatNum(transferPrice.transfer_fiyati)} TL/birim</strong>
                  </div>
                  {estimatedTotal !== null && (
                    <div>
                      <span style={{ color: colors.textSecondary }}>Tahmini Toplam:</span>{' '}
                      <strong style={{ color: '#166534' }}>{formatNum(estimatedTotal)} TL</strong>
                    </div>
                  )}
                </div>
              ) : (
                <span style={{ color: '#92400e', fontWeight: 500 }}>Transfer fiyati tanimli degil</span>
              )}
            </div>
          )}

          {/* Quantity */}
          <div style={{ marginBottom: '16px' }}>
            <label style={formLabelStyle}>Miktar</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              required
              style={{ ...formInputStyle, maxWidth: '200px' }}
              placeholder="0.00"
            />
          </div>

          {/* Tarih */}
          <div style={{ marginBottom: '20px' }}>
            <label style={formLabelStyle}>Transfer Tarihi</label>
            <input
              type="date"
              value={transferTarihi}
              onChange={e => setTransferTarihi(e.target.value)}
              required
              style={{ ...formInputStyle, maxWidth: '250px' }}
            />
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '20px' }}>
            <label style={formLabelStyle}>Notlar (opsiyonel)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{ ...formInputStyle, minHeight: '80px', resize: 'vertical' }}
              placeholder="Aciklama ekleyin..."
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={formLoading || !materialId}
            style={{
              width: '100%', padding: '12px', fontSize: '15px', fontWeight: 600,
              border: 'none', borderRadius: radius.sm, cursor: (formLoading || !materialId) ? 'not-allowed' : 'pointer',
              color: '#fff', background: (formLoading || !materialId) ? '#ccc' : styles.primary,
              transition: 'background 0.15s',
            }}
          >
            {formLoading ? 'Olusturuluyor...' : 'Transfer Talebi Olustur'}
          </button>
        </form>
      </div>
    );
  }
};

// ======================== STYLES ========================
const labelSmall = { display: 'block', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b', marginBottom: '4px' };
const selectStyle = { width: '100%', padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', background: '#fff' };
const thStyle = { padding: '6px 8px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b', borderBottom: `2px solid ${colors.border}` };
const tdStyle = { padding: '10px 12px', verticalAlign: 'middle' };
const formLabelStyle = { display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textSecondary, marginBottom: '6px' };
const formInputStyle = { width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box' };
const sectionTitleStyle = { fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textTertiary, marginBottom: '10px' };
const detailGridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' };
const detailLabelStyle = { fontWeight: 600, color: colors.textSecondary };

const primaryBtnStyle = {
  padding: '8px 16px', background: colors.primary, color: '#fff',
  border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600, fontSize: '13px',
  whiteSpace: 'nowrap',
};
const secondaryBtnStyle = {
  padding: '8px 16px', background: '#fff', color: colors.textSecondary,
  border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600, fontSize: '13px',
  whiteSpace: 'nowrap',
};

export default InventoryPage;
